(() => {
  const itemsFrom = (body) => Array.isArray(body?.items) ? body.items : body?.id ? [body] : [];
  const lineTotal = (item) => {
    if (Number.isFinite(Number(item?.final_line_price))) return Number(item.final_line_price);
    if (Number.isFinite(Number(item?.final_price))) return Number(item.final_price) * Number(item.quantity || 1);
    if (Number.isFinite(Number(item?.line_price))) return Number(item.line_price);
    return Number(item?.price || 0) * Number(item?.quantity || 1);
  };
  const variantId = (item) => Number(item?.variant_id || item?.id || 0);
  const productId = (item) => Number(item?.product_id || 0);
  const kachingMetadata = (item) => {
    const raw = item?.properties?.__kaching_bundles;
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };
  const matches = ({
    result,
    state,
    groupId,
    submittedProperties,
    giftMode,
    kaching,
    cartGiftVariantId,
    realGiftIdentities = [],
    discountedLinePrice
  }) => {
    if (!result?.ok || !state || !groupId) return false;
    const items = itemsFrom(result.body);
    const needsKachingGift = giftMode !== 'companion_only';
    const usesRealGifts = giftMode === 'personalized_real_gifts';
    const paidItemCount = state.kind === 'full_set' ? 3 : state.kind === 'harness_bungee' ? 2 : 1;
    const expectedItemCount = paidItemCount + (usesRealGifts ? 3 : needsKachingGift ? 1 : 0);
    if (items.length !== expectedItemCount || items.some((item) => Number(item?.quantity || 0) !== 1)) return false;
    const mainItem = items.find((item) => {
      const properties = item.properties || {};
      return String(properties._safewalk_package_group || '') === String(groupId)
        && properties._safewalk_package_role === 'main'
        && properties._safewalk_package === state.kind
        && Number(properties._safewalk_package_discount) === Number(state.discountPercent)
        && Number(properties._safewalk_expected_total_cents) === Number(state.total);
    });
    if (!mainItem || variantId(mainItem) !== Number(state.mainVariant.id)) return false;
    const publicProperties = Object.entries(submittedProperties || {})
      .filter(([key, value]) => !key.startsWith('_') && value);
    if (publicProperties.some(([key, value]) => String(mainItem.properties?.[key] || '') !== String(value))) return false;
    const mainMetadata = kachingMetadata(mainItem);
    const returnedMainBarId = state.kind === 'harness_only' ? mainMetadata?.bid : mainMetadata?.bar;
    if (!mainMetadata
      || mainMetadata.deal !== kaching.deal
      || returnedMainBarId !== state.barId
      || mainMetadata.main !== true
      || !String(mainMetadata.id || '')) return false;
    const privateGroupId = String(mainMetadata.id);
    if (lineTotal(mainItem) !== discountedLinePrice(state.mainVariant.price, state.discountPercent)) return false;
    const expectedPaidItems = [];
    if (state.kind === 'harness_bungee' || state.kind === 'full_set') {
      expectedPaidItems.push({
        variantId: Number(state.bungeeVariant?.id || 0),
        productId: kaching.bungeeProductId,
        bundleProduct: state.kind === 'full_set' ? kaching.p3Bungee : kaching.p2Bungee,
        lineTotal: discountedLinePrice(state.bungeeVariant?.price, state.discountPercent)
      });
    }
    if (state.kind === 'full_set') {
      expectedPaidItems.push({
        variantId: Number(state.collarVariant?.id || 0),
        productId: kaching.collarProductId,
        bundleProduct: kaching.p3Collar,
        lineTotal: discountedLinePrice(state.collarVariant?.price, state.discountPercent)
      });
    }
    const paidItems = expectedPaidItems.map((expected) => items.find((item) => {
      const metadata = kachingMetadata(item);
      return item !== mainItem
        && variantId(item) === expected.variantId
        && (!expected.productId || productId(item) === expected.productId)
        && lineTotal(item) === expected.lineTotal
        && metadata?.deal === kaching.deal
        && metadata?.bar === state.barId
        && String(metadata?.id || '') === privateGroupId
        && metadata?.bundleProduct === expected.bundleProduct;
    }));
    if (paidItems.some((item) => !item) || new Set(paidItems).size !== paidItems.length) return false;
    if (usesRealGifts) {
      const giftIds = kaching.realGiftIds?.[state.kind] || {};
      const expectedGifts = realGiftIdentities.map((identity) => ({
        ...identity,
        giftId: giftIds[identity.role] || ''
      }));
      if (expectedGifts.length !== 3 || expectedGifts.some((gift) => !gift.giftId)) return false;
      const giftItems = expectedGifts.map((expected) => items.find((item) => {
        const metadata = kachingMetadata(item);
        return item !== mainItem
          && variantId(item) === Number(expected.variantId)
          && productId(item) === Number(expected.productId)
          && lineTotal(item) === 0
          && metadata?.deal === kaching.deal
          && String(metadata?.id || '') === privateGroupId
          && metadata?.gift === expected.giftId;
      }));
      if (giftItems.some((item) => !item)
        || new Set(giftItems).size !== giftItems.length
        || giftItems.some((item) => paidItems.includes(item))) return false;
    } else if (needsKachingGift) {
      const expectedGiftId = state.kind === 'full_set'
        ? kaching.p3Gift
        : state.kind === 'harness_bungee'
          ? kaching.p2Gift
          : kaching.p1Gift;
      const giftItem = items.find((item) => {
        const metadata = kachingMetadata(item);
        return item !== mainItem
          && variantId(item) === cartGiftVariantId
          && (!kaching.giftProductId || productId(item) === kaching.giftProductId)
          && lineTotal(item) === 0
          && metadata?.deal === kaching.deal
          && String(metadata?.id || '') === privateGroupId
          && metadata?.gift === expectedGiftId;
      });
      if (!giftItem || paidItems.includes(giftItem)) return false;
    }
    if (!items.every((item) => String(kachingMetadata(item)?.id || '') === privateGroupId)) return false;
    return items.reduce((sum, item) => sum + lineTotal(item), 0) === Number(state.total);
  };

  window.SafeWalkV51CartResponse = Object.freeze({
    itemsFrom,
    lineTotal,
    metadataFor: kachingMetadata,
    matches
  });
})();
